import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Button,
  Chip,
  Divider,
  TextField,
} from '@mui/material';
import {
  WhatsApp as WhatsAppIcon,
  People as PeopleIcon,
  Message as MessageIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  Phone as PhoneIcon,
  Cloud as CloudIcon,
  Schedule as ScheduleIcon,
  DateRange as DateRangeIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import axios from '../config/axios';
import { API_CONFIG } from '../config/api';

interface WhatsAppDashboardStats {
  // Leads
  total_leads: number;
  leads_today: number;
  leads_yesterday: number;
  leads_24h: number;
  leads_3days: number;
  leads_7days: number;
  converted_leads: number;
  conversion_rate: number;
  conversion_rate_24h: number;
  stage_1_count: number;
  stage_2_count: number;
  stage_3_count: number;
  stage_4_count: number;
  // Conversas
  total_conversations: number;
  conversations_24h: number;
  conversations_pending: number;
  conversations_active: number;
  conversations_completed: number;
  conversations_abandoned: number;
  conversations_whatsapp_onboarding?: number;
  conversations_whatsapp_recovery?: number;
  conversations_cloud_api?: number;
  // Instâncias
  instances_total?: number;
  instances_connected?: number;
  cloud_api_configs_total?: number;
  cloud_api_configs_active?: number;
  // Mensagens
  total_messages_sent?: number;
  total_messages_received?: number;
  // Outros
  total_disparos: number;
  webhook_events_24h?: number;
  calculated_at?: string;
}

// Interface de compatibilidade com o formato antigo (para fallback)
interface WhatsAppDashboardDataLegacy {
  leads: {
    total: number;
    new: number;
    converted: number;
    pending: number;
  };
  conversations: {
    total: number;
    active: number;
    completed: number;
    abandoned: number;
  };
  instances: {
    total: number;
    connected: number;
    disconnected: number;
    lastActivity: string;
  };
  performance: {
    conversionRate: number;
  };
}

const WhatsAppDashboard: React.FC = () => {
  const [stats, setStats] = useState<WhatsAppDashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  const [isFromCache, setIsFromCache] = useState(false);

  // Estado para busca de leads por intervalo de datas
  const [customDateFrom, setCustomDateFrom] = useState<string>('');
  const [customDateTo, setCustomDateTo] = useState<string>('');
  const [customLeadsCount, setCustomLeadsCount] = useState<number | null>(null);
  const [loadingCustom, setLoadingCustom] = useState(false);

  useEffect(() => {
    fetchWhatsAppDashboardStats();
  }, []);

  const fetchWhatsAppDashboardStats = async () => {
    try {
      setLoading(true);
      setError(null);

      // Usar o novo endpoint otimizado com cache
      const response = await axios.get(`${API_CONFIG.BASE_URL}/whatsapp-dashboard/stats`);
      console.log('📊 [WhatsApp Dashboard] Response completa:', response.data);
      
      if (response.data.success && response.data.data) {
        const statsData = response.data.data;
        console.log('📈 [WhatsApp Dashboard] Stats do cache:', statsData);
        
        setStats(statsData);
        setIsFromCache(response.data.message?.includes('cache') || false);
        setLastUpdate(statsData.calculated_at ? new Date(statsData.calculated_at) : new Date());
      } else {
        throw new Error(response.data.error || 'Erro ao carregar estatísticas');
      }
    } catch (err: any) {
      console.error('Erro ao buscar stats do dashboard WhatsApp:', err);
      
      // Fallback: tentar o endpoint antigo se o novo falhar
      try {
        console.log('⚠️ [WhatsApp Dashboard] Tentando fallback para endpoint antigo...');
        const fallbackResponse = await axios.get(`${API_CONFIG.BASE_URL}/whatsapp-dashboard/data`);
        
        if (fallbackResponse.data.success && fallbackResponse.data.data) {
          const legacyData: WhatsAppDashboardDataLegacy = fallbackResponse.data.data;
          
          // Converter formato antigo para o novo
          const convertedStats: WhatsAppDashboardStats = {
            total_leads: legacyData.leads.total,
            leads_today: legacyData.leads.new,
            leads_yesterday: 0,
            leads_24h: legacyData.leads.new,
            leads_3days: 0,
            leads_7days: 0,
            converted_leads: legacyData.leads.converted,
            conversion_rate: legacyData.performance.conversionRate,
            conversion_rate_24h: 0,
            stage_1_count: 0,
            stage_2_count: 0,
            stage_3_count: 0,
            stage_4_count: 0,
            total_conversations: legacyData.conversations.total,
            conversations_24h: 0,
            conversations_pending: 0,
            conversations_active: legacyData.conversations.active,
            conversations_completed: legacyData.conversations.completed,
            conversations_abandoned: legacyData.conversations.abandoned,
            instances_total: legacyData.instances.total,
            instances_connected: legacyData.instances.connected,
            total_disparos: 0,
          };
          
          setStats(convertedStats);
          setIsFromCache(false);
          setLastUpdate(new Date());
          console.log('✅ [WhatsApp Dashboard] Dados carregados via fallback');
          return;
        }
      } catch (fallbackErr) {
        console.error('❌ [WhatsApp Dashboard] Fallback também falhou:', fallbackErr);
      }
      
      setError('Erro ao carregar dados do dashboard WhatsApp. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    try {
      setLoading(true);
      // Forçar atualização do cache
      await axios.post(`${API_CONFIG.BASE_URL}/whatsapp-dashboard/refresh-stats`);
      // Buscar dados atualizados
      await fetchWhatsAppDashboardStats();
    } catch (err) {
      console.error('Erro ao atualizar stats:', err);
      // Se falhar ao forçar atualização, tentar buscar do cache mesmo assim
      await fetchWhatsAppDashboardStats();
    }
  };

  const handleSearchCustomDateRange = async () => {
    if (!customDateFrom || !customDateTo) {
      alert('Por favor, selecione a data inicial e final');
      return;
    }
    if (new Date(customDateFrom) > new Date(customDateTo)) {
      alert('Data inicial não pode ser maior que data final');
      return;
    }
    try {
      setLoadingCustom(true);
      const response = await axios.get(`${API_CONFIG.BASE_URL}/whatsapp-dashboard/leads-by-date-range`, {
        params: { date_from: customDateFrom, date_to: customDateTo },
        timeout: 15000,
      });
      if (response.data.success) {
        setCustomLeadsCount(response.data.data.count);
      } else {
        throw new Error(response.data.error || 'Erro ao buscar leads');
      }
    } catch (err: any) {
      console.error('Erro ao buscar leads por intervalo:', err);
      alert(err.response?.data?.error || 'Erro ao buscar leads no intervalo selecionado');
      setCustomLeadsCount(null);
    } finally {
      setLoadingCustom(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={fetchWhatsAppDashboardStats}>
            Tentar Novamente
          </Button>
        }>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!stats) {
    return (
      <Box p={3}>
        <Alert severity="info">
          Nenhum dado encontrado para o dashboard WhatsApp.
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center">
          <WhatsAppIcon sx={{ fontSize: 32, color: '#25D366', mr: 2 }} />
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              Dashboard WhatsApp
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body2" color="text.secondary">
                Última atualização: {formatDate(lastUpdate.toISOString())}
              </Typography>
              {isFromCache && (
                <Chip 
                  label="Cache" 
                  size="small" 
                  color="success" 
                  variant="outlined"
                  icon={<ScheduleIcon sx={{ fontSize: 14 }} />}
                />
              )}
            </Box>
          </Box>
        </Box>
        <IconButton onClick={handleRefresh} disabled={loading} title="Atualizar dados">
          <RefreshIcon />
        </IconButton>
      </Box>

      {/* Métricas Principais - Linha 1 */}
      <Grid container spacing={3}>
        {/* Total de Leads */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            height: '100%',
            background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
            color: 'white',
            boxShadow: '0 4px 20px rgba(37, 211, 102, 0.3)',
            borderRadius: 2,
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 30px rgba(37, 211, 102, 0.4)',
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {stats.total_leads.toLocaleString()}
                  </Typography>
                  <Typography variant="body1" sx={{ opacity: 0.9 }}>
                    Total de Leads
                  </Typography>
                </Box>
                <PeopleIcon sx={{ fontSize: 40, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Leads Hoje */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            height: '100%',
            background: 'linear-gradient(135deg, #2196F3 0%, #1976D2 100%)',
            color: 'white',
            boxShadow: '0 4px 20px rgba(33, 150, 243, 0.3)',
            borderRadius: 2,
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 30px rgba(33, 150, 243, 0.4)',
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {stats.leads_today}
                  </Typography>
                  <Typography variant="body1" sx={{ opacity: 0.9 }}>
                    Leads Hoje
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.7 }}>
                    24h: {stats.leads_24h} | 7d: {stats.leads_7days}
                  </Typography>
                </Box>
                <WhatsAppIcon sx={{ fontSize: 40, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Total de Conversas */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            height: '100%',
            background: 'linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)',
            color: 'white',
            boxShadow: '0 4px 20px rgba(76, 175, 80, 0.3)',
            borderRadius: 2,
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 30px rgba(76, 175, 80, 0.4)',
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {stats.total_conversations.toLocaleString()}
                  </Typography>
                  <Typography variant="body1" sx={{ opacity: 0.9 }}>
                    Conversas
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.7 }}>
                    24h: {stats.conversations_24h} | Ativas: {stats.conversations_active}
                  </Typography>
                </Box>
                <MessageIcon sx={{ fontSize: 40, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Taxa de Conversão */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            height: '100%',
            background: 'linear-gradient(135deg, #9C27B0 0%, #7B1FA2 100%)',
            color: 'white',
            boxShadow: '0 4px 20px rgba(156, 39, 176, 0.3)',
            borderRadius: 2,
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 30px rgba(156, 39, 176, 0.4)',
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {stats.conversion_rate?.toFixed(1) || '0.0'}%
                  </Typography>
                  <Typography variant="body1" sx={{ opacity: 0.9 }}>
                    Taxa de Conversão
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.7 }}>
                    {stats.converted_leads} convertidos
                  </Typography>
                </Box>
                <TrendingUpIcon sx={{ fontSize: 40, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Métricas Secundárias - Linha 2 */}
      <Grid container spacing={3} sx={{ mt: 1 }}>
        {/* Instâncias Evolution/Z-API */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            height: '100%',
            background: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)',
            color: 'white',
            boxShadow: '0 4px 20px rgba(255, 152, 0, 0.3)',
            borderRadius: 2,
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 30px rgba(255, 152, 0, 0.4)',
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {stats.instances_connected || 0}/{stats.instances_total || 0}
                  </Typography>
                  <Typography variant="body1" sx={{ opacity: 0.9 }}>
                    Instâncias Conectadas
                  </Typography>
                </Box>
                <PhoneIcon sx={{ fontSize: 40, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Cloud API Configs */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            height: '100%',
            background: 'linear-gradient(135deg, #00BCD4 0%, #0097A7 100%)',
            color: 'white',
            boxShadow: '0 4px 20px rgba(0, 188, 212, 0.3)',
            borderRadius: 2,
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 30px rgba(0, 188, 212, 0.4)',
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {stats.cloud_api_configs_active || 0}/{stats.cloud_api_configs_total || 0}
                  </Typography>
                  <Typography variant="body1" sx={{ opacity: 0.9 }}>
                    Cloud API Ativas
                  </Typography>
                </Box>
                <CloudIcon sx={{ fontSize: 40, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Mensagens Enviadas (24h) */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            height: '100%',
            background: 'linear-gradient(135deg, #607D8B 0%, #455A64 100%)',
            color: 'white',
            boxShadow: '0 4px 20px rgba(96, 125, 139, 0.3)',
            borderRadius: 2,
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 30px rgba(96, 125, 139, 0.4)',
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {(stats.total_messages_sent || 0).toLocaleString()}
                  </Typography>
                  <Typography variant="body1" sx={{ opacity: 0.9 }}>
                    Mensagens Enviadas (24h)
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.7 }}>
                    Recebidas: {(stats.total_messages_received || 0).toLocaleString()}
                  </Typography>
                </Box>
                <MessageIcon sx={{ fontSize: 40, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Disparos */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            height: '100%',
            background: 'linear-gradient(135deg, #E91E63 0%, #C2185B 100%)',
            color: 'white',
            boxShadow: '0 4px 20px rgba(233, 30, 99, 0.3)',
            borderRadius: 2,
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 30px rgba(233, 30, 99, 0.4)',
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {stats.total_disparos}
                  </Typography>
                  <Typography variant="body1" sx={{ opacity: 0.9 }}>
                    Total de Disparos
                  </Typography>
                </Box>
                <WhatsAppIcon sx={{ fontSize: 40, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Detalhes de Conversas e Estágios */}
      <Grid container spacing={3} sx={{ mt: 1 }}>
        {/* Conversas por Tipo */}
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#25D366' }}>
                Conversas por Tipo
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <Box textAlign="center" p={2} sx={{ bgcolor: 'rgba(37, 211, 102, 0.1)', borderRadius: 2 }}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#25D366' }}>
                      {stats.conversations_whatsapp_onboarding || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">Onboarding</Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box textAlign="center" p={2} sx={{ bgcolor: 'rgba(255, 152, 0, 0.1)', borderRadius: 2 }}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#FF9800' }}>
                      {stats.conversations_whatsapp_recovery || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">Recovery</Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box textAlign="center" p={2} sx={{ bgcolor: 'rgba(0, 188, 212, 0.1)', borderRadius: 2 }}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#00BCD4' }}>
                      {stats.conversations_cloud_api || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">Cloud API</Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Leads por Estágio */}
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#25D366' }}>
                Leads por Estágio do CRM
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={3}>
                  <Box textAlign="center" p={2} sx={{ bgcolor: 'rgba(33, 150, 243, 0.1)', borderRadius: 2 }}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#2196F3' }}>
                      {stats.stage_1_count}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">Estágio 1</Typography>
                  </Box>
                </Grid>
                <Grid item xs={3}>
                  <Box textAlign="center" p={2} sx={{ bgcolor: 'rgba(255, 193, 7, 0.1)', borderRadius: 2 }}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#FFC107' }}>
                      {stats.stage_2_count}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">Estágio 2</Typography>
                  </Box>
                </Grid>
                <Grid item xs={3}>
                  <Box textAlign="center" p={2} sx={{ bgcolor: 'rgba(255, 152, 0, 0.1)', borderRadius: 2 }}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#FF9800' }}>
                      {stats.stage_3_count}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">Estágio 3</Typography>
                  </Box>
                </Grid>
                <Grid item xs={3}>
                  <Box textAlign="center" p={2} sx={{ bgcolor: 'rgba(76, 175, 80, 0.1)', borderRadius: 2 }}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#4CAF50' }}>
                      {stats.stage_4_count}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">Estágio 4</Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Status das Conversas */}
      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid item xs={12}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#25D366' }}>
                Status das Conversas
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={3}>
                  <Box textAlign="center" p={2} sx={{ bgcolor: 'rgba(255, 193, 7, 0.1)', borderRadius: 2 }}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#FFC107' }}>
                      {stats.conversations_pending}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">Pendentes</Typography>
                  </Box>
                </Grid>
                <Grid item xs={3}>
                  <Box textAlign="center" p={2} sx={{ bgcolor: 'rgba(33, 150, 243, 0.1)', borderRadius: 2 }}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#2196F3' }}>
                      {stats.conversations_active}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">Ativas</Typography>
                  </Box>
                </Grid>
                <Grid item xs={3}>
                  <Box textAlign="center" p={2} sx={{ bgcolor: 'rgba(76, 175, 80, 0.1)', borderRadius: 2 }}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#4CAF50' }}>
                      {stats.conversations_completed}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">Completas</Typography>
                  </Box>
                </Grid>
                <Grid item xs={3}>
                  <Box textAlign="center" p={2} sx={{ bgcolor: 'rgba(244, 67, 54, 0.1)', borderRadius: 2 }}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#F44336' }}>
                      {stats.conversations_abandoned}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">Abandonadas</Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Buscar Leads por Intervalo de Datas */}
      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid item xs={12}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#25D366', mb: 3 }}>
                <DateRangeIcon sx={{ fontSize: 20, mr: 1, verticalAlign: 'middle' }} />
                Buscar Leads por Intervalo de Datas
              </Typography>
              <Divider sx={{ mb: 3 }} />
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={4} md={3}>
                  <TextField
                    label="Data Inicial"
                    type="date"
                    value={customDateFrom}
                    onChange={(e) => setCustomDateFrom(e.target.value)}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ max: customDateTo || undefined }}
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={3}>
                  <TextField
                    label="Data Final"
                    type="date"
                    value={customDateTo}
                    onChange={(e) => setCustomDateTo(e.target.value)}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: customDateFrom || undefined }}
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={3}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSearchCustomDateRange}
                    disabled={loadingCustom || !customDateFrom || !customDateTo}
                    startIcon={loadingCustom ? <CircularProgress size={20} /> : <SearchIcon />}
                    fullWidth
                    sx={{ height: '56px', bgcolor: '#25D366', '&:hover': { bgcolor: '#128C7E' } }}
                  >
                    {loadingCustom ? 'Buscando...' : 'Buscar'}
                  </Button>
                </Grid>
                <Grid item xs={12} sm={12} md={3}>
                  {customLeadsCount !== null && (
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: 'rgba(37, 211, 102, 0.1)',
                        borderRadius: 2,
                        border: '2px solid #25D366',
                        textAlign: 'center',
                      }}
                    >
                      <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#25D366' }}>
                        {customLeadsCount.toLocaleString()}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Leads encontrados
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        {customDateFrom} até {customDateTo}
                      </Typography>
                    </Box>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default WhatsAppDashboard;